/**
 * Solana Decoding Module
 *
 * Provides transaction decoding, summarization, and pre-sign verification for
 * Solana Stake Program transactions. Supports both legacy and versioned (v0) transactions.
 *
 * @example
 * ```ts
 * import { DecodingSDK } from "@blockdaemon/transaction-verifier";
 *
 * const sdk = new DecodingSDK({ logLevel: "info" });
 * const solana = sdk.solanaDecodingModule;
 *
 * // Summarize a transaction
 * const summary = solana.summarizeUnsignedTransaction(base64Tx);
 * console.log(summary.feePayer, summary.instructions);
 *
 * // Validate with rules
 * const result = solana.validateUnsignedTransaction(base64Tx, {
 *   requireDelegate: true,
 *   forbidAuthorize: true,
 * });
 * ```
 *
 * @module
 */
import { getLogger, } from "../../shared/logger.js";
import { decodeSolanaTransaction, decodeStakeInstruction, hasStakeActivation, isStakeProgram, isSystemProgram, readU32LE, STAKE_PROGRAM_ID_B58, StakeInstructionTag, SYSTEM_PROGRAM_ID_B58, toBase58, } from "./decoding/impl/decoder.js";
import { buildAssertionBuilderFromUnsigned as _buildAssertionBuilderFromUnsigned, decodeUnsignedTransaction as _decodeUnsignedTransaction, summarizeUnsignedTransaction as _summarizeUnsignedTransaction, validateUnsignedTransaction as _validateUnsignedTransaction, } from "./decoding/impl/sdk.js";
import { extractStakeVotePubkey, findInstructionsByProgramId, getInstructionType, hasStakeAuthorize, hasStakeDelegate, hasStakeLockupChange, InstructionType, summarizeTransaction, TxAssertionBuilder, } from "./decoding/impl/tx-utils.js";
/**
 * Solana decoding and verification module.
 *
 * Provides:
 * - **Transaction decoding**: Parse raw or base64-encoded Solana transactions
 * - **Summarization**: Get human-readable summaries with decoded Stake/System instructions
 * - **Validation**: Verify transactions against customizable rules before signing
 * - **Instruction analysis**: Inspect and filter instructions by type or program
 * - **Utility functions**: Base58 encoding, instruction parsing, program identification
 *
 * @example Summarize a staking transaction
 * ```ts
 * const summary = solana.summarizeUnsignedTransaction(base64Tx);
 * console.log("Fee payer:", summary.feePayer);
 * console.log("Instructions:", summary.instructions.length);
 *
 * for (const ix of summary.instructions) {
 *   if (ix.programKind === "stake") {
 *     console.log(`Stake instruction: ${ix.stake?.name}`);
 *   } else if (ix.programKind === "system") {
 *     console.log(`System instruction: ${ix.system?.name}`);
 *   }
 * }
 * ```
 *
 * @example Validate before signing
 * ```ts
 * const { ok, errors, summary } = solana.validateUnsignedTransaction(base64Tx, {
 *   requireDelegate: true,
 *   forbidAuthorize: true,
 *   instructionCount: { min: 1, max: 5 },
 *   allowOnlyInstructions: [
 *     InstructionType.SystemCreateAccount,
 *     InstructionType.StakeInitialize,
 *     InstructionType.StakeDelegate,
 *   ],
 * });
 *
 * if (!ok) {
 *   console.error("Validation failed:", errors);
 *   throw new Error(errors.join(", "));
 * }
 * ```
 *
 * @example Fluent validation with assertion builder
 * ```ts
 * const builder = solana.buildAssertionBuilderFromUnsigned(base64Tx);
 * builder
 *   .requireStakeDelegate()
 *   .forbidStakeAuthorize()
 *   .feePayerIs(expectedFeePayer)
 *   .requireStakeVotePubkey(expectedVotePubkey)
 *   .instructionCountBetween(3, 4)
 *   .run(); // throws ValidationError if any assertion fails
 * ```
 */
export class SolanaDecodingModule {
    logger;
    constructor(config) {
        const logOptions = {
            label: "solana-decoding-module",
            level: config.logLevel,
        };
        this.logger = getLogger(logOptions);
        this.logger.addContext("module", "solana-decoding-module");
    }
    // ================================
    // Transaction Decoding Methods
    // ================================
    /**
     * Decodes an unsigned Solana transaction from base64 or hex string.
     *
     * Automatically detects the input format (base64 or hex) and parses the
     * transaction structure including message header, accounts, and instructions.
     * Supports both legacy and versioned (v0) transactions.
     *
     * @param b64OrHex - Transaction bytes as base64 (preferred) or hex string
     * @returns Decoded transaction with message structure and signatures
     *
     * @example
     * ```ts
     * const tx = solana.decodeUnsignedTransaction(base64Tx);
     * console.log("Version:", tx.message.version);
     * console.log("Accounts:", tx.message.accountKeys.length);
     * console.log("Instructions:", tx.message.instructions.length);
     *
     * // Check if fee payer is a signer
     * const feePayerIdx = tx.message.feePayerIndex;
     * console.log("Fee payer is signer:", tx.message.isSigner[feePayerIdx]);
     * ```
     */
    decodeUnsignedTransaction(b64OrHex) {
        this.logger.debug("decodeUnsignedTransaction", { inputLength: b64OrHex.length });
        return _decodeUnsignedTransaction(b64OrHex);
    }
    /**
     * Decodes a raw Solana transaction from bytes or base64 string.
     *
     * Lower-level decoder that accepts raw bytes directly. Use this when you
     * already have the transaction as a `Uint8Array`.
     *
     * @param rawOrB64 - Raw transaction bytes or base64 string
     * @returns Decoded transaction with full message structure
     *
     * @example
     * ```ts
     * // From raw bytes
     * const txBytes = new Uint8Array([...]);
     * const tx = solana.decodeSolanaTransaction(txBytes);
     *
     * // From base64 string
     * const tx2 = solana.decodeSolanaTransaction(base64String);
     * ```
     */
    decodeSolanaTransaction(rawOrB64) {
        this.logger.debug("decodeSolanaTransaction", {
            inputLength: typeof rawOrB64 === "string" ? rawOrB64.length : rawOrB64.byteLength,
        });
        return decodeSolanaTransaction(rawOrB64);
    }
    // ================================
    // Transaction Summarization Methods
    // ================================
    /**
     * Creates a human-readable summary of an unsigned transaction.
     *
     * Parses the transaction and returns a structured summary including fee payer,
     * all accounts with their roles, and decoded instructions. Stake and System
     * program instructions are automatically parsed with relevant metadata.
     *
     * @param b64OrHex - Transaction bytes as base64 (preferred) or hex string
     * @returns Transaction summary with accounts, instructions, and metadata
     *
     * @example
     * ```ts
     * const summary = solana.summarizeUnsignedTransaction(base64Tx);
     *
     * console.log("Fee payer:", summary.feePayer);
     * console.log("Recent blockhash:", summary.recentBlockhash);
     *
     * // Inspect accounts
     * for (const acc of summary.accounts) {
     *   console.log(`${acc.pubkey}: signer=${acc.isSigner}, writable=${acc.isWritable}`);
     * }
     *
     * // Inspect instructions
     * for (const ix of summary.instructions) {
     *   console.log(`Program: ${ix.programId} (${ix.programKind})`);
     *   if (ix.stake) {
     *     console.log(`  Stake: ${ix.stake.name}`);
     *     if (ix.stake.stakeAuthority) {
     *       console.log(`  Stake authority: ${ix.stake.stakeAuthority}`);
     *     }
     *   }
     *   if (ix.system) {
     *     console.log(`  System: ${ix.system.name}`);
     *     if (ix.system.amount) {
     *       console.log(`  Amount: ${ix.system.amount} lamports`);
     *     }
     *   }
     * }
     * ```
     */
    summarizeUnsignedTransaction(b64OrHex) {
        this.logger.debug("summarizeUnsignedTransaction", { inputLength: b64OrHex.length });
        return _summarizeUnsignedTransaction(b64OrHex);
    }
    /**
     * Creates a summary from an already-decoded transaction.
     *
     * Use this when you've already decoded the transaction and want to generate
     * the summary without re-parsing.
     *
     * @param tx - Previously decoded transaction
     * @returns Transaction summary with accounts, instructions, and metadata
     *
     * @example
     * ```ts
     * const tx = solana.decodeUnsignedTransaction(base64Tx);
     * // ... do some analysis on tx ...
     * const summary = solana.summarizeTransaction(tx);
     * console.log(summary.feePayer);
     * ```
     */
    summarizeTransaction(tx) {
        this.logger.debug("summarizeTransaction", {
            instructionCount: tx.message.instructions.length,
        });
        return summarizeTransaction(tx);
    }
    // ================================
    // Validation Methods
    // ================================
    /**
     * Validates an unsigned transaction against a configurable set of rules.
     *
     * Use this to verify transactions before signing. Returns a result object
     * with validation status and any errors, without throwing exceptions.
     *
     * **Available Rules:**
     * - `requireAuthorize` / `forbidAuthorize`: Stake authorize instruction presence
     * - `requireDelegate` / `forbidDelegate`: Stake delegate instruction presence
     * - `feePayer`: Fee payer must match this pubkey
     * - `usesProgram`: Transaction must use these programs
     * - `instructionCount`: Instruction count must be within range
     * - `allowOnlyInstructions`: Whitelist of allowed instruction types
     * - `customize`: Custom validation function using the assertion builder
     *
     * @param b64OrHex - Transaction bytes as base64 (preferred) or hex string
     * @param rules - Validation rules to apply (all optional)
     * @returns Validation result with `ok` status, `errors` array, and `summary`
     *
     * @example Basic validation
     * ```ts
     * const { ok, errors } = solana.validateUnsignedTransaction(base64Tx, {
     *   requireDelegate: true,
     *   forbidAuthorize: true,
     * });
     *
     * if (!ok) {
     *   throw new Error(`Transaction invalid: ${errors.join(", ")}`);
     * }
     * ```
     *
     * @example Comprehensive validation
     * ```ts
     * const result = solana.validateUnsignedTransaction(base64Tx, {
     *   requireDelegate: true,
     *   forbidAuthorize: true,
     *   feePayer: expectedFeePayer,
     *   instructionCount: { min: 3, max: 4 },
     *   allowOnlyInstructions: [
     *     InstructionType.SystemAdvanceNonce,
     *     InstructionType.SystemCreateAccount,
     *     InstructionType.StakeInitialize,
     *     InstructionType.StakeDelegate,
     *   ],
     *   customize: (builder, summary) => {
     *     builder
     *       .requireStakeVotePubkey(expectedVotePubkey)
     *       .requireStakeAuthorities(stakeAuth, withdrawAuth);
     *   },
     * });
     *
     * console.log("Valid:", result.ok);
     * console.log("Fee payer:", result.summary.feePayer);
     * ```
     */
    validateUnsignedTransaction(b64OrHex, rules = {}) {
        this.logger.debug("validateUnsignedTransaction", {
            inputLength: b64OrHex.length,
            rules: Object.keys(rules),
        });
        return _validateUnsignedTransaction(b64OrHex, rules);
    }
    /**
     * Creates a fluent assertion builder for custom validation.
     *
     * The builder provides a chainable API for validating transaction properties.
     * Use this when you need fine-grained control over validation logic.
     * Call `.run()` at the end to execute validations and throw on failures.
     *
     * **Available Assertions:**
     * - `requireStakeAuthorize()` / `forbidStakeAuthorize()`
     * - `requireStakeDelegate()` / `forbidStakeDelegate()`
     * - `forbidStakeLockupChange()`
     * - `requireStakeAuthorities(stakeAuth, withdrawAuth)`
     * - `requireStakeVotePubkey(votePubkey)`
     * - `requireStakeAuthorityIsSigner(expectedAuth?)`
     * - `feePayerIs(pubkey)`
     * - `usesProgram(programId)`
     * - `instructionCountBetween(min, max)`
     * - `allowOnlyInstructions(types[])`
     * - `requireCanonicalStakeFlow()`
     *
     * @param b64OrHex - Transaction bytes as base64 (preferred) or hex string
     * @returns Chainable assertion builder
     * @throws ValidationError when `.run()` is called and assertions fail
     *
     * @example
     * ```ts
     * const builder = solana.buildAssertionBuilderFromUnsigned(base64Tx);
     *
     * builder
     *   .requireStakeDelegate()
     *   .forbidStakeAuthorize()
     *   .feePayerIs(expectedFeePayer)
     *   .requireStakeVotePubkey(expectedVotePubkey)
     *   .instructionCountBetween(3, 4)
     *   .run(); // throws if any assertion fails
     *
     * // Access the summary without running validations
     * const summary = builder.getSummary();
     * ```
     *
     * @example Canonical stake flow validation
     * ```ts
     * // Validates: AdvanceNonce -> CreateAccount -> Initialize -> Delegate
     * solana.buildAssertionBuilderFromUnsigned(base64Tx)
     *   .requireCanonicalStakeFlow()
     *   .requireStakeAuthorityIsSigner(expectedStakeAuth)
     *   .run();
     * ```
     */
    buildAssertionBuilderFromUnsigned(b64OrHex) {
        this.logger.debug("buildAssertionBuilderFromUnsigned", { inputLength: b64OrHex.length });
        return _buildAssertionBuilderFromUnsigned(b64OrHex);
    }
    // ================================
    // Instruction Analysis Methods
    // ================================
    /**
     * Gets the instruction type at a specific index.
     *
     * Returns a typed enum value identifying the instruction. Recognizes all
     * Stake and System program instructions.
     *
     * @param tx - Decoded transaction
     * @param index - Zero-based instruction index
     * @returns Instruction type enum value
     *
     * @example
     * ```ts
     * const tx = solana.decodeUnsignedTransaction(base64Tx);
     *
     * for (let i = 0; i < tx.message.instructions.length; i++) {
     *   const type = solana.getInstructionType(tx, i);
     *   console.log(`Instruction ${i}: ${type}`);
     *
     *   if (type === InstructionType.StakeDelegate) {
     *     console.log("Found delegation instruction!");
     *   }
     * }
     * ```
     */
    getInstructionType(tx, index) {
        this.logger.debug("getInstructionType", { index });
        return getInstructionType(tx, index);
    }
    /**
     * Finds all instruction indices for a specific program.
     *
     * Useful for locating all instructions that interact with a particular program.
     *
     * @param tx - Decoded transaction
     * @param programIdB58 - Program ID in base58 format
     * @returns Array of zero-based instruction indices
     *
     * @example
     * ```ts
     * const tx = solana.decodeUnsignedTransaction(base64Tx);
     *
     * // Find all Stake program instructions
     * const stakeIxIndices = solana.findInstructionsByProgramId(
     *   tx,
     *   solana.STAKE_PROGRAM_ID
     * );
     * console.log("Stake instructions at indices:", stakeIxIndices);
     *
     * // Find all System program instructions
     * const systemIxIndices = solana.findInstructionsByProgramId(
     *   tx,
     *   solana.SYSTEM_PROGRAM_ID
     * );
     * console.log("System instructions at indices:", systemIxIndices);
     * ```
     */
    findInstructionsByProgramId(tx, programIdB58) {
        this.logger.debug("findInstructionsByProgramId", { programIdB58 });
        return findInstructionsByProgramId(tx, programIdB58);
    }
    /**
     * Extracts the vote account pubkey from a stake delegation instruction.
     *
     * Searches for a `Stake.Delegate` instruction and returns the vote account
     * that stake is being delegated to.
     *
     * @param tx - Decoded transaction
     * @returns Vote account pubkey in base58, or `undefined` if no delegation found
     *
     * @example
     * ```ts
     * const tx = solana.decodeUnsignedTransaction(base64Tx);
     * const votePubkey = solana.extractStakeVotePubkey(tx);
     *
     * if (votePubkey) {
     *   console.log("Delegating to validator:", votePubkey);
     *
     *   // Verify it's the expected validator
     *   if (votePubkey !== expectedVotePubkey) {
     *     throw new Error("Unexpected vote account");
     *   }
     * }
     * ```
     */
    extractStakeVotePubkey(tx) {
        this.logger.debug("extractStakeVotePubkey");
        return extractStakeVotePubkey(tx);
    }
    // ================================
    // Transaction Check Methods
    // ================================
    /**
     * Checks if the transaction contains any stake authorize instruction.
     *
     * Authorize instructions change the stake or withdraw authority of a stake
     * account. This is often forbidden in security-sensitive contexts.
     *
     * **Detected instructions:**
     * - `Authorize`
     * - `AuthorizeWithSeed`
     * - `AuthorizeChecked`
     * - `AuthorizeCheckedWithSeed`
     *
     * @param tx - Decoded transaction
     * @returns `true` if any authorize instruction is present
     *
     * @example
     * ```ts
     * const tx = solana.decodeUnsignedTransaction(base64Tx);
     *
     * if (solana.hasStakeAuthorize(tx)) {
     *   throw new Error("Transaction attempts to change stake authorities!");
     * }
     * ```
     */
    hasStakeAuthorize(tx) {
        this.logger.debug("hasStakeAuthorize");
        return hasStakeAuthorize(tx);
    }
    /**
     * Checks if the transaction contains a stake delegate instruction.
     *
     * Delegate instructions activate stake by assigning it to a validator's
     * vote account.
     *
     * @param tx - Decoded transaction
     * @returns `true` if a delegate instruction is present
     *
     * @example
     * ```ts
     * const tx = solana.decodeUnsignedTransaction(base64Tx);
     *
     * if (!solana.hasStakeDelegate(tx)) {
     *   throw new Error("Expected a staking transaction");
     * }
     * ```
     */
    hasStakeDelegate(tx) {
        this.logger.debug("hasStakeDelegate");
        return hasStakeDelegate(tx);
    }
    /**
     * Checks if the transaction activates stake.
     *
     * Alias for `hasStakeDelegate()`. Stake activation occurs when stake is
     * delegated to a vote account.
     *
     * @param tx - Decoded transaction
     * @returns `true` if stake activation instruction is present
     *
     * @example
     * ```ts
     * const tx = solana.decodeUnsignedTransaction(base64Tx);
     *
     * if (solana.hasStakeActivation(tx)) {
     *   console.log("This transaction will activate stake");
     * }
     * ```
     */
    hasStakeActivation(tx) {
        this.logger.debug("hasStakeActivation");
        return hasStakeActivation(tx);
    }
    /**
     * Checks if the transaction contains a lockup change instruction.
     *
     * Lockup changes can restrict when stake can be withdrawn. This is often
     * forbidden in security-sensitive contexts.
     *
     * **Detected instructions:**
     * - `SetLockup`
     * - `SetLockupChecked`
     *
     * @param tx - Decoded transaction
     * @returns `true` if a lockup change instruction is present
     *
     * @example
     * ```ts
     * const tx = solana.decodeUnsignedTransaction(base64Tx);
     *
     * if (solana.hasStakeLockupChange(tx)) {
     *   throw new Error("Transaction attempts to change stake lockup!");
     * }
     * ```
     */
    hasStakeLockupChange(tx) {
        this.logger.debug("hasStakeLockupChange");
        return hasStakeLockupChange(tx);
    }
    // ================================
    // Utility Methods
    // ================================
    /**
     * Converts a byte array to base58 string.
     *
     * Solana uses base58 encoding for public keys, signatures, and other identifiers.
     *
     * @param bytes - Byte array to encode
     * @returns Base58 encoded string
     *
     * @example
     * ```ts
     * const pubkeyBytes = new Uint8Array(32);
     * const pubkeyB58 = solana.toBase58(pubkeyBytes);
     * console.log(pubkeyB58); // "11111111111111111111111111111111"
     * ```
     */
    toBase58(bytes) {
        return toBase58(bytes);
    }
    /**
     * Reads a 32-bit unsigned integer in little-endian format.
     *
     * Useful for parsing Solana instruction data which uses little-endian encoding.
     *
     * @param d - Byte array to read from
     * @param offset - Byte offset to start reading (default: 0)
     * @returns 32-bit unsigned integer value
     *
     * @example
     * ```ts
     * const instructionData = new Uint8Array([2, 0, 0, 0]); // tag = 2 (Delegate)
     * const tag = solana.readU32LE(instructionData, 0);
     * console.log(tag); // 2
     * ```
     */
    readU32LE(d, offset = 0) {
        return readU32LE(d, offset);
    }
    /**
     * Decodes a Stake program instruction's data.
     *
     * Parses the instruction data to extract the instruction tag, human-readable
     * name, and whether it's an authorize-type instruction.
     *
     * @param data - Raw instruction data bytes
     * @returns Decoded instruction info with `tag`, `name`, and `isAuthorize`
     *
     * @example
     * ```ts
     * const tx = solana.decodeUnsignedTransaction(base64Tx);
     * const ix = tx.message.instructions[0];
     * const stakeIx = solana.decodeStakeInstruction(ix.data);
     *
     * console.log(`Tag: ${stakeIx.tag}`);       // e.g., 2
     * console.log(`Name: ${stakeIx.name}`);     // e.g., "Delegate"
     * console.log(`IsAuth: ${stakeIx.isAuthorize}`); // false
     * ```
     */
    decodeStakeInstruction(data) {
        this.logger.debug("decodeStakeInstruction", { dataLength: data.length });
        return decodeStakeInstruction(data);
    }
    /**
     * Checks if a program public key is the Stake program.
     *
     * @param programKey - Program public key bytes (32 bytes)
     * @returns `true` if this is the Stake program
     *
     * @example
     * ```ts
     * const tx = solana.decodeUnsignedTransaction(base64Tx);
     * const ix = tx.message.instructions[0];
     * const programKey = tx.message.accountKeys[ix.programIdIndex];
     *
     * if (solana.isStakeProgram(programKey)) {
     *   console.log("This is a Stake program instruction");
     * }
     * ```
     */
    isStakeProgram(programKey) {
        return isStakeProgram(programKey);
    }
    /**
     * Checks if a program public key is the System program.
     *
     * @param programKey - Program public key bytes (32 bytes)
     * @returns `true` if this is the System program
     *
     * @example
     * ```ts
     * const tx = solana.decodeUnsignedTransaction(base64Tx);
     * const ix = tx.message.instructions[0];
     * const programKey = tx.message.accountKeys[ix.programIdIndex];
     *
     * if (solana.isSystemProgram(programKey)) {
     *   console.log("This is a System program instruction");
     * }
     * ```
     */
    isSystemProgram(programKey) {
        return isSystemProgram(programKey);
    }
    // ================================
    // Constants
    // ================================
    /**
     * Stake program ID in base58 format.
     *
     * @example
     * ```ts
     * console.log(solana.STAKE_PROGRAM_ID);
     * // "Stake11111111111111111111111111111111111111"
     * ```
     */
    get STAKE_PROGRAM_ID() {
        return STAKE_PROGRAM_ID_B58;
    }
    /**
     * System program ID in base58 format.
     *
     * @example
     * ```ts
     * console.log(solana.SYSTEM_PROGRAM_ID);
     * // "11111111111111111111111111111111"
     * ```
     */
    get SYSTEM_PROGRAM_ID() {
        return SYSTEM_PROGRAM_ID_B58;
    }
    /**
     * Stake instruction tag constants.
     *
     * Use these to identify specific Stake program instructions by their tag number.
     *
     * @example
     * ```ts
     * const tags = solana.StakeInstructionTag;
     * console.log(tags.Initialize);  // 0
     * console.log(tags.Delegate);    // 2
     * console.log(tags.Deactivate);  // 5
     * console.log(tags.Withdraw);    // 4
     * ```
     */
    get StakeInstructionTag() {
        return StakeInstructionTag;
    }
    /**
     * Instruction type enum for use in validation rules.
     *
     * Use these values with `allowOnlyInstructions` validation rule or when
     * analyzing instruction types.
     *
     * @example
     * ```ts
     * const types = solana.InstructionType;
     *
     * // Use in validation
     * solana.validateUnsignedTransaction(base64Tx, {
     *   allowOnlyInstructions: [
     *     types.SystemCreateAccount,
     *     types.StakeInitialize,
     *     types.StakeDelegate,
     *   ],
     * });
     *
     * // Compare instruction types
     * const tx = solana.decodeUnsignedTransaction(base64Tx);
     * const type = solana.getInstructionType(tx, 0);
     * if (type === types.StakeDelegate) {
     *   console.log("First instruction is delegation");
     * }
     * ```
     */
    get InstructionType() {
        return InstructionType;
    }
}
// Re-export enums and classes
export { InstructionType, StakeInstructionTag, TxAssertionBuilder };
//# sourceMappingURL=data:application/json;base64,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